<template>
  <div>
    <a-table
      :columns="tableColumns"
      :dataSource="points"
      bordered
      :pagination="false"
      size="small"
      rowKey="pointName"
    >
      <template slot="measureTime" slot-scope="text, record">
        <a-date-picker
          style="width: 100%"
          format="YYYY-MM-DD HH:mm"
          :show-time="{ format: 'HH:mm' }"
          v-model="record.measureTime"
          :disabled="disableSubmit"
        />
      </template>
      <template slot="standardValue" slot-scope="text, record">
        <a-input-number
          v-model="record.standardValue"
          :disabled="disableSubmit"
          style="width: 100%"
          @change="() => calcPointError(record)"
        />
      </template>
      <template slot="measuredValue" slot-scope="text, record">
        <a-input-number
          v-model="record.measuredValue"
          :disabled="disableSubmit"
          style="width: 100%"
          @change="() => calcPointError(record)"
        />
      </template>
      <template slot="errorValue" slot-scope="text, record">
        <div v-if="record.errorValue != null">
          <div v-if="record.errorName && errorMode !== 'st31'" style="color: #999; font-size: 12px;">{{ record.errorName }}</div>
          <span>{{ record.errorValue }}{{ record.errorType === 'relative' && errorMode !== 'st31' ? '%' : (errorMode === 'st31' ? '' : (' ' + (unit || ''))) }}</span>
        </div>
        <span v-else>--</span>
      </template>
      <template slot="qualified" slot-scope="text, record">
        <a-select v-model="record.qualified" :disabled="disableSubmit" placeholder="选择" style="width: 90px">
          <a-select-option value="yes">合格</a-select-option>
          <a-select-option value="no">不合格</a-select-option>
        </a-select>
      </template>
      <!-- ST31专用列：响应时间 -->
      <template v-if="showResponseTime" slot="responseT1" slot-scope="text, record">
        <a-input-number v-model="record.responseT1" :disabled="disableSubmit" style="width: 100%" @change="() => calcResponseTotal(record)" />
      </template>
      <template v-if="showResponseTime" slot="responseT2" slot-scope="text, record">
        <a-input-number v-model="record.responseT2" :disabled="disableSubmit" style="width: 100%" @change="() => calcResponseTotal(record)" />
      </template>
      <template v-if="showResponseTime" slot="responseTotal" slot-scope="text, record">
        <span>{{ record.responseTotal != null ? record.responseTotal + ' s' : '--' }}</span>
      </template>
    </a-table>
  </div>
</template>

<script>
import moment from 'moment'
export default {
  name: 'QcPointTable',
  props: {
    /** 多点配置 [{pointName:'零点',pointSort:1},...] */
    pointConfig: { type: Array, default: () => [] },
    /** 初始多点数据（编辑时传入） */
    initPoints: { type: Array, default: () => [] },
    disableSubmit: { type: Boolean, default: false },
    unit: { type: String, default: '' },
    /** 误差计算模式: 'st21' | 'st31' */
    errorMode: { type: String, default: 'st21' },
    /** 量程最高值（ST21多点线性核查用） */
    rangeMax: { type: Number, default: null },
    /** 是否显示响应时间列（ST31季度专用） */
    showResponseTime: { type: Boolean, default: false },
    /** 仪器量程值（ST31季度多点核查用） */
    instrumentRange: { type: Number, default: null },
    /** 核查因子编码 */
    factorCode: { type: String, default: '' },
  },
  data() {
    return {
      points: [],
    }
  },
  computed: {
    tableColumns() {
      let isSt31 = this.errorMode === 'st31'
      let unitStr = this.unit ? `(${this.unit})` : ''
      let cols = [
        { title: '多点', dataIndex: 'pointLabel', width: 70, align: 'center' },
        { title: '测量时间', dataIndex: 'measureTime', scopedSlots: { customRender: 'measureTime' }, width: 200 },
        { title: isSt31 ? `标准气体参考值${unitStr}` : `标准物质浓度${unitStr}`, dataIndex: 'standardValue', scopedSlots: { customRender: 'standardValue' }, width: 110 },
        { title: isSt31 ? `CEMS显示值${unitStr}` : `测量值${unitStr}`, dataIndex: 'measuredValue', scopedSlots: { customRender: 'measuredValue' }, width: 110 },
        { title: isSt31 ? '示值误差(%)' : '误差', dataIndex: 'errorValue', scopedSlots: { customRender: 'errorValue' }, width: 90 },
        { title: '合格', dataIndex: 'qualified', scopedSlots: { customRender: 'qualified' }, width: 100 },
      ]
      if (this.showResponseTime) {
        cols.splice(5, 0, {
          title: '响应时间测定值(s)',
          children: [
            { title: 'T1', dataIndex: 'responseT1', scopedSlots: { customRender: 'responseT1' }, width: 80, align: 'center' },
            { title: 'T2', dataIndex: 'responseT2', scopedSlots: { customRender: 'responseT2' }, width: 80, align: 'center' },
            { title: 'T=T1+T2', dataIndex: 'responseTotal', scopedSlots: { customRender: 'responseTotal' }, width: 90, align: 'center' },
          ]
        })
      }
      return cols
    },
  },
  watch: {
    pointConfig: {
      immediate: true,
      handler() { this.initPointData() },
    },
    initPoints: {
      immediate: true,
      handler(val) {
        if (val && val.length > 0) {
          this.points = val.map((p, i) => {
            let errorName = ''
            if (this.errorMode === 'st21') {
              errorName = this.getErrorNameForSt21(p.standardValue)
            } else if (this.errorMode === 'st31') {
              errorName = '示值误差'
            }
            return {
              ...p,
              measureTime: p.measureTime ? moment(p.measureTime) : null,
              errorName,
              errorValue: p.errorValue != null ? p.errorValue : (p.indicationError != null ? p.indicationError : null),
              errorType: p.errorType || null,
              pointLabel: this.getLabelByName(p.pointName),
            }
          })
          
          this.$nextTick(() => {
            this.points.forEach(row => this.calcPointError(row))
          })
        }
      },
    },
    rangeMax() {
      // 量程最高值变化时重新计算所有点的误差
      this.$nextTick(() => {
        this.points.forEach(row => this.calcPointError(row))
      })
    },
    instrumentRange() {
      this.$nextTick(() => {
        this.points.forEach(row => this.calcPointError(row))
      })
    },
    factorCode() {
      this.$nextTick(() => {
        this.points.forEach(row => this.calcPointError(row))
      })
    },
  },
  methods: {
    /** 初始化空多点数据行 */
    initPointData() {
      if (this.initPoints && this.initPoints.length > 0) return
      this.points = this.pointConfig.map((cfg) => ({
        pointName: cfg.pointName,
        pointSort: cfg.pointSort,
        pointLabel: this.getLabelByName(cfg.pointName),
        measureTime: null,
        standardValue: null,
        measuredValue: null,
        errorValue: null,
        errorType: null,
        qualified: undefined,
        responseT1: null,
        responseT2: null,
        responseTotal: null,
      }))
    },
    /** 映射多点名称→显示标签 */
    getLabelByName(name) {
      const map = { zero: '零点', low: '低点', mid: '中点', high: '高点' }
      return map[name] || name
    },
    /** ST21：根据标准物质浓度与量程最高值20%的关系判断误差类型 */
    getErrorNameForSt21(standardValue) {
      if (standardValue == null || this.rangeMax == null || this.rangeMax === 0) return ''
      let threshold = this.rangeMax * 0.2
      return standardValue > threshold ? '相对误差' : '绝对误差'
    },
    /** 计算单点误差 */
    calcPointError(row) {
      if (row.standardValue == null || row.measuredValue == null) {
        row.errorValue = null
        row.errorType = null
        row.errorName = ''
        return
      }
      if (this.errorMode === 'st21') {
        // ST21多点：标准物质浓度 > 量程最高值的20% → 相对误差，否则 → 绝对误差
        let threshold = (this.rangeMax != null && this.rangeMax !== 0) ? this.rangeMax * 0.2 : null
        if (threshold != null && row.standardValue > threshold) {
          if (row.standardValue === 0) { row.errorValue = null; return }
          row.errorValue = this.truncate2(((row.measuredValue - row.standardValue) / row.standardValue) * 100)
          row.errorType = 'relative'
          row.errorName = '相对误差'
        } else {
          row.errorValue = Number((row.measuredValue - row.standardValue).toFixed(4))
          row.errorType = 'absolute'
          row.errorName = '绝对误差'
        }
      } else if (this.errorMode === 'st31') {
        // ST31示值误差：当非甲烷总烃(a24088)仪器量程<=100，或SO2(a21026)量程<286，或NOx(a21002)量程<410时，分母为仪器量程值
        let base = row.standardValue
        if (this.instrumentRange != null && this.factorCode) {
          if (this.factorCode === 'a24088' && this.instrumentRange <= 100) {
            base = this.instrumentRange
          } else if (this.factorCode === 'a21026' && this.instrumentRange < 286) {
            base = this.instrumentRange
          } else if (this.factorCode === 'a21002' && this.instrumentRange < 410) {
            base = this.instrumentRange
          }
        }
        if (base === 0 || base == null) { row.errorValue = null; return }
        row.errorValue = this.truncate2(((row.measuredValue - row.standardValue) / base) * 100)
        row.errorType = 'relative'
        row.errorName = '示值误差'
      }
      this.$emit('point-change', this.points)
    },
    /** 计算响应时间合计 */
    calcResponseTotal(row) {
      if (row.responseT1 != null && row.responseT2 != null) {
        row.responseTotal = this.truncate2(row.responseT1 + row.responseT2)
      } else {
        row.responseTotal = null
      }
      this.$emit('point-change', this.points)
    },
    /** 截断两位小数 */
    truncate2(val) {
      if (val == null) return null
      return Math.trunc(val * 100) / 100
    },
    /** 获取所有多点数据（供父组件收集） */
    getPointsData() {
      return this.points.map(({ pointLabel, ...rest }) => {
        if (this.errorMode === 'st31') {
          rest.indicationError = rest.errorValue
        }
        return rest
      })
    },
  },
}
</script>
